import { useRef, useState } from 'react';
import { ContainerCheckoutIntent, ContainerExtraSelection, User } from '@back4app2/sdk';
import back4app2 from '../back4app2';
import { Eyebrow } from '../components/ds';
import { ChevronDownIcon } from '../components/ds/icons';
import { formatMoney } from '../containers/PlanAndExtras';
import usePaddleCheckout from './usePaddleCheckout';

/**
 * Buying a container app's plan and extras — the ONLY implementation.
 *
 * There used to be two ways to pay for the same app. The creation wizard used
 * this flow; every other entry point — the "Pay Now" on a pending app, the
 * upgrade links — sent the customer to the pricing site, which runs the legacy
 * checkout and reports back to a webhook host hardcoded to production. An app
 * paid that way stayed "Pending Payment" forever here, and the extras it had
 * chosen were never on the subscription: that path only knows about plans.
 *
 * So the logic lives here, and the screens render it. A second copy is how the
 * two drift again.
 */

export type PurchaseOutcome =
  | { ok: true }
  | { ok: false; reason: 'CLOSED' | 'FAILED'; message?: string };

interface PurchaseArgs {
  appId: string;
  planId: string;
  /** One pack of each — the creation wizard. */
  featureIds?: string[];
  /** Extras with a pack count — Settings, and paying for a pending app. */
  extras?: ContainerExtraSelection[];
  user: User;
  /** The screen the checkout was opened from, on its funnel events. */
  source: string;
  /** What the summary column shows. Money the customer is about to spend has
   *  to be itemised where they are about to spend it. */
  summary: {
    planName: string;
    planPrice: number;
    lines: Array<{ id: string; name: string; detail?: string; price: number }>;
    /** Overrides the default "pay for <plan>" label above the plan name. */
    eyebrow?: string;
    /** One sentence under the total, for what this screen needs to add. */
    note?: string;
  };
}

/**
 * `lazy`: pass it on a screen that may run another Paddle checkout (Settings,
 * where the legacy plan change initializes its own). Paddle keeps one event
 * callback per page, so this one is taken when the checkout opens, not when
 * the screen renders.
 */
export default function useContainerPurchase(
  options: {
    lazy?: boolean;
    /** A plain dim behind the checkout, without the blur. */
    plainOverlay?: boolean;
  } = {}
) {
  const { configured, openCheckout, closeCheckout } = usePaddleCheckout({
    lazy: options.lazy,
  });
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  // Phone only: whether the line items are open under the price.
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [summary, setSummary] = useState<PurchaseArgs['summary'] | undefined>();
  // Who is waiting on the checkout. Paddle's `checkout.closed` does not arrive
  // when WE tear the frame down, so our own ✕ has to settle the wait itself.
  const resolveRef = useRef<((outcome: PurchaseOutcome) => void) | null>(null);

  const settle = (outcome: PurchaseOutcome) => {
    const resolve = resolveRef.current;

    resolveRef.current = null;
    setIsCheckoutOpen(false);
    resolve?.(outcome);
  };

  const purchase = async (args: PurchaseArgs): Promise<PurchaseOutcome> => {
    const { appId, planId, featureIds, extras, user } = args;
    let intent: ContainerCheckoutIntent;

    try {
      intent = await back4app2.containerCheckoutIntent(planId, featureIds, extras);
    } catch (e) {
      console.error('could not resolve the checkout intent', e);

      return {
        ok: false,
        reason: 'FAILED',
        message: "We couldn't reach the payment service.",
      };
    }

    // Always a checkout: the subscription belongs to the app, a new or pending
    // app has none, and Paddle creates subscriptions from checkouts only.
    if (!configured) {
      console.error('checkout requested but Paddle is not configured');

      return {
        ok: false,
        reason: 'FAILED',
        message: 'Checkout is unavailable right now.',
      };
    }

    let customerId: string | undefined;

    try {
      // Locks the email on the checkout: one back4app account, one Paddle
      // customer. Called only here, because it CREATES a customer.
      customerId = await back4app2.ensureContainersPaddleCustomer();
    } catch (e) {
      console.error('could not resolve the Paddle customer', e);
    }

    return await new Promise<PurchaseOutcome>((resolve) => {
      resolveRef.current = resolve;
      setSummary(args.summary);
      setIsDetailsOpen(false);
      setIsCheckoutOpen(true);

      // Paddle draws into a node that has to exist already, and the state
      // change above is what creates it.
      window.setTimeout(() => {
        openCheckout({
          // Lines with their quantity: two packs of one extra are ONE Paddle
          // item with quantity 2, never the same price twice.
          items: intent.items,
          frameTarget: 'checkout-container',
          customerId: customerId || intent.customerId || undefined,
          customerEmail: user.username,
          // Only what something on the other side reads: appId and planId for
          // the checkout app's record, userId for the ownership check.
          customData: { appId, userId: user.id },
          onCompleted: (transactionId, checkoutId) => {
            back4app2
              .confirmContainerCheckout(appId, planId, featureIds, transactionId, checkoutId)
              // The card was charged either way. `false` only means Paddle had
              // not materialised the subscription yet — offering "continue on
              // Free" to someone who just paid would be the worst answer.
              .then(() => settle({ ok: true }))
              .catch(() => settle({ ok: true }));
          },
          onClosed: () => settle({ ok: false, reason: 'CLOSED' }),
          tracking: { source: args.source, app_id: appId, plan_id: planId },
        });
      }, 200);
    });
  };

  const total =
    (summary?.planPrice || 0) +
    (summary?.lines || []).reduce((sum, line) => sum + line.price, 0);

  const lines = (
    <>
      <ul className="list-none m-0 p-0 border-t border-navy-600">
        <li className="flex items-baseline justify-between gap-4 py-3 border-b border-navy-600">
          <span className="min-w-0 text-ds-135 text-navy-100">{summary?.planName}</span>
          <span className="flex-none font-courrier text-ds-115 text-navy-200">
            {formatMoney(summary?.planPrice || 0)}/mo
          </span>
        </li>
        {(summary?.lines || []).map((line) => (
          <li key={line.id} className="flex items-baseline justify-between gap-4 py-3 border-b border-navy-600">
            <span className="min-w-0 text-ds-135 text-navy-100">
              {line.name}
              {line.detail ? (
                <span className="ml-2 font-courrier text-ds-105 text-navy-300">{line.detail}</span>
              ) : null}
            </span>
            <span className="flex-none font-courrier text-ds-115 text-navy-200">
              {formatMoney(line.price)}/mo
            </span>
          </li>
        ))}
      </ul>

      <div className="flex items-baseline justify-between gap-4 pt-4">
        <span className="text-ds-135 text-white font-medium">Total today</span>
        <span className="font-courrier text-ds-15 text-white">{formatMoney(total)}</span>
      </div>

      {summary?.note ? (
        <p className="m-0 mt-4 text-ds-115 leading-[1.5] text-navy-300">{summary.note}</p>
      ) : null}
    </>
  );

  const checkoutModal = isCheckoutOpen ? (
    <>
      <div
        className={`fixed inset-0 z-[10000] ${
          options.plainOverlay ? 'bg-black/70' : 'bg-black/70 backdrop-blur-sm'
        }`}
      />
      <div className="fixed inset-0 z-[10001] flex items-start justify-center overflow-y-auto p-2 md:p-4 lg:p-8">
        <div className="relative w-full max-w-[980px] my-auto bg-navy-800 border border-navy-400 rounded-card shadow-[0_14px_48px_rgba(0,0,0,0.5)] overflow-hidden grid grid-cols-1 md:grid-cols-2">
          {/* On the card, not in a column: on a phone the columns stack and
              the Paddle column's corner is halfway down the page. */}
          <button
            type="button"
            onClick={() => {
              // Both, in this order: tear down Paddle's frame, then settle
              // the wait ourselves — inline, `checkout.closed` does not come.
              closeCheckout();
              settle({ ok: false, reason: 'CLOSED' });
            }}
            aria-label="Close checkout"
            className="b4-focus absolute top-3 right-3 z-10 h-8 w-8 rounded-control inline-flex items-center justify-center text-navy-200 hover:text-white hover:bg-navy-600 transition-colors duration-[120ms]"
          >
            ✕
          </button>

          {/* Ours: what is being bought, item by item. Paddle's inline frame
              renders only the payment form — the summary is our job, and an
              overlay checkout would bring its own and duplicate it.

              On a phone it is a head over the form rather than a column
              beside it: the plan, the price, and the lines behind "Show
              details" — the card form is what the screen is for, and a
              full itemisation above it pushed the form below the fold. */}
          <div className="min-w-0 px-4 pt-5 pb-4 md:p-6 lg:p-8 bg-navy-900 border-b md:border-b-0 md:border-r border-navy-600 flex flex-col">
            <div className="md:hidden flex flex-col items-center text-center">
              <span className="text-ds-135 text-navy-200">{summary?.planName}</span>
              <div className="mt-1 flex items-baseline gap-1.5">
                <span className="font-sora text-[32px] leading-none text-white">{formatMoney(total)}</span>
                <span className="text-ds-115 text-navy-300">/month</span>
              </div>
              <p className="m-0 mt-2 text-ds-115 leading-[1.5] text-navy-300">
                Billed monthly · cancel any time
              </p>
              <button
                type="button"
                onClick={() => setIsDetailsOpen((open) => !open)}
                aria-expanded={isDetailsOpen}
                className="b4-focus mt-3 inline-flex items-center gap-1.5 h-9 px-4 rounded-control bg-navy-700 text-ds-125 font-medium text-white hover:bg-navy-600 transition-colors duration-[120ms]"
              >
                {isDetailsOpen ? 'Hide details' : 'Show details'}
                <ChevronDownIcon
                  size={14}
                  className={`transition-transform duration-[120ms] ${isDetailsOpen ? 'rotate-180' : ''}`}
                />
              </button>
            </div>

            <div className="hidden md:block">
              <Eyebrow>{summary?.eyebrow || (summary ? `pay for ${summary.planName}` : 'payment')}</Eyebrow>
              <h2 className="m-0 mt-2 font-sora text-[28px] leading-[1.15] text-white">
                {summary?.planName}
              </h2>

              <div className="mt-5 flex items-baseline gap-1.5">
                <span className="font-sora text-[32px] leading-none text-white">{formatMoney(total)}</span>
                <span className="text-ds-115 text-navy-300">/month</span>
              </div>
              <p className="m-0 mt-2 text-ds-115 leading-[1.5] text-navy-300">
                Billed monthly · cancel any time
              </p>
            </div>

            <div className={`${isDetailsOpen ? 'block' : 'hidden'} md:block mt-4 md:mt-6`}>
              {lines}
            </div>

            <div className="hidden md:block mt-auto pt-8">
              <div className="font-courrier text-[length:calc(11px*var(--b4-type,1))] tracking-[0.08em] uppercase text-navy-400">
                powered by
              </div>
              <div className="mt-1 font-sora text-ds-135 text-navy-200">Paddle</div>
              <div className="mt-1 font-courrier text-[length:calc(11px*var(--b4-type,1))] tracking-[0.08em] uppercase text-navy-400">
                merchant of record · pci dss level 1
              </div>
            </div>
          </div>

          {/* Paddle's */}
          <div className="min-w-0 relative p-4 lg:p-6">
            <div className="checkout-container w-full min-h-[450px]" />
          </div>
        </div>
      </div>
    </>
  ) : null;

  return { configured, purchase, checkoutModal };
}
